import React, { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../../store/authStore';
import { validateEmail } from '../../lib/utils';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import ForgotPasswordForm from './ForgotPasswordForm';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'login' | 'register' | 'forgot-password';
  onSwitchMode: (mode: 'login' | 'register' | 'forgot-password') => void;
  initialEmail?: string;
  isInvitationFlow?: boolean;
}

interface PasswordStrength {
  score: number;
  label: string;
  color: string;
  checks: {
    length: boolean;
    uppercase: boolean;
    lowercase: boolean;
    number: boolean;
    special: boolean;
  };
}

const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  mode,
  onSwitchMode,
  initialEmail,
  isInvitationFlow = false
}) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName1: '',
    lastName2: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength | null>(null);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [policiesAccepted, setPoliciesAccepted] = useState(false);
  const [sensitiveDataAccepted, setSensitiveDataAccepted] = useState(false);

  const emailInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const { signIn, signUp, signUpViaInvitation, signInWithGoogle, resetPassword } = useAuthStore();

  useEffect(() => {
    // PRO-328: only auto-focus on desktop. On mobile, auto-focusing the email
    // input pops the on-screen keyboard immediately, which covers the bottom
    // half of the form (including the Google OAuth CTA). Mobile users tap the
    // field themselves when they want the keyboard.
    if (isOpen && emailInputRef.current) {
      const isDesktop = typeof window !== 'undefined'
        && window.matchMedia('(min-width: 1024px)').matches;
      if (isDesktop) {
        setTimeout(() => emailInputRef.current?.focus(), 100);
      }
    }
  }, [isOpen, mode]);

  useEffect(() => {
    if (!isOpen) {
      setFormData({ email: '', password: '', confirmPassword: '', firstName: '', lastName1: '', lastName2: '' });
      setErrors({});
      setShowPassword(false);
      setShowConfirmPassword(false);
      setPasswordStrength(null);
      setResetEmailSent(false);
      setPoliciesAccepted(false);
      setSensitiveDataAccepted(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (initialEmail && isOpen && mode === 'register') {
      setFormData(prev => ({ ...prev, email: initialEmail }));
    }
  }, [initialEmail, isOpen, mode]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !loading) {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, loading]);

  useEffect(() => {
    if (mode === 'register' && formData.password) {
      setPasswordStrength(calculatePasswordStrength(formData.password));
    } else {
      setPasswordStrength(null);
    }
  }, [formData.password, mode]);

  // Clear consent error automatically once both boxes are ticked, so the user
  // sees immediate feedback they've satisfied the requirement.
  useEffect(() => {
    if (policiesAccepted && sensitiveDataAccepted && errors.consent) {
      setErrors((prev) => {
        const { consent, ...rest } = prev;
        return rest;
      });
    }
  }, [policiesAccepted, sensitiveDataAccepted, errors.consent]);

  // Focuses the first unchecked consent checkbox so keyboard users land on the
  // thing they need to fix. The Variant B layout keeps consent above the fold
  // on iPhone SE, so no scrolling is needed — we only focus, never scroll.
  const surfaceConsentError = () => {
    requestAnimationFrame(() => {
      const block = document.getElementById('consent-block');
      const firstUnchecked = block?.querySelector<HTMLInputElement>('input[type="checkbox"]:not(:checked)');
      firstUnchecked?.focus({ preventScroll: true });
    });
  };

  // Validates that both consent checkboxes are ticked. Used by both the email
  // submit path (inside handleSubmit) and the Google OAuth click path (via
  // onValidateConsent prop passed to RegisterForm).
  const validateConsent = (): boolean => {
    if (!policiesAccepted || !sensitiveDataAccepted) {
      setErrors((prev) => ({
        ...prev,
        consent: 'Marca ambas casillas para continuar.'
      }));
      surfaceConsentError();
      return false;
    }
    return true;
  };

  const calculatePasswordStrength = (password: string): PasswordStrength => {
    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[^A-Za-z0-9]/.test(password)
    };

    const score = Object.values(checks).filter(Boolean).length;

    let label = 'Muy débil';
    let color = 'bg-rose-500';

    if (score >= 5) {
      label = 'Fuerte';
      color = 'bg-emerald-500';
    } else if (score >= 4) {
      label = 'Media';
      color = 'bg-amber-400';
    } else if (score >= 2) {
      label = 'Débil';
      color = 'bg-rose-500';
    }

    return { score, label, color, checks };
  };

  const handleGoogleSignIn = async (code: string) => {
    if (!code) {
      setErrors({ submit: 'Error de conexión con Google' });
      return;
    }
    setLoading(true);
    setErrors({});
    try {
      const now = mode === 'register' ? new Date().toISOString() : undefined;
      const policiesTs = mode === 'register' && policiesAccepted ? now : undefined;
      const sensitiveTs = mode === 'register' && sensitiveDataAccepted ? now : undefined;

      const result = await signInWithGoogle(code, policiesTs, sensitiveTs);
      if (!result.success) {
        setErrors({ submit: result.error || 'Error al iniciar sesión con Google' });
      }
    } catch (error) {
      setErrors({ submit: 'Error de conexión con Google' });
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const newErrors: Record<string, string> = {};

    if (!validateEmail(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (mode !== 'forgot-password') {
      if (formData.password.length < 8) {
        newErrors.password = 'La contraseña debe tener al menos 8 caracteres';
      }

      if (mode === 'register') {
        if (!passwordStrength) {
          newErrors.password = 'La contraseña no cumple los requisitos';
        } else {
          const { checks } = passwordStrength;
          if (!checks.length) {
            newErrors.password = 'La contraseña debe tener al menos 8 caracteres';
          } else if (!checks.uppercase) {
            newErrors.password = 'La contraseña debe contener al menos una letra mayúscula';
          } else if (!checks.lowercase) {
            newErrors.password = 'La contraseña debe contener al menos una letra minúscula';
          } else if (!checks.number) {
            newErrors.password = 'La contraseña debe contener al menos un número';
          } else if (!checks.special) {
            newErrors.password = 'La contraseña debe contener al menos un símbolo';
          }
        }

        if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = 'Las contraseñas no coinciden';
        }

        if (!formData.firstName.trim()) {
          newErrors.firstName = 'El nombre es requerido';
        }

        if (!formData.lastName1.trim()) {
          newErrors.lastName1 = 'El primer apellido es requerido';
        }

        if (!policiesAccepted || !sensitiveDataAccepted) {
          newErrors.consent = 'Marca ambas casillas para continuar.';
        }
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      if (newErrors.consent) {
        surfaceConsentError();
      }
      return;
    }

    setLoading(true);

    try {
      if (mode === 'forgot-password') {
        const result = await resetPassword(formData.email);
        if (result.success) {
          setResetEmailSent(true);
        } else {
          setErrors({ submit: result.error || 'Error al enviar el correo' });
        }
      } else {
        let result;

        if (mode === 'login') {
          result = await signIn(formData.email, formData.password);
        } else {
          const consentTs = new Date().toISOString();
          const policiesTs = policiesAccepted ? consentTs : undefined;
          const sensitiveTs = sensitiveDataAccepted ? consentTs : undefined;

          if (isInvitationFlow) {
            const invitationToken = sessionStorage.getItem('pending_invitation_token');

            if (invitationToken) {
              result = await signUpViaInvitation(
                invitationToken,
                formData.email,
                formData.password,
                formData.firstName,
                formData.lastName1,
                formData.lastName2,
                policiesTs,
                sensitiveTs
              );
            } else {
              result = await signUp(
                formData.email,
                formData.password,
                formData.firstName,
                formData.lastName1,
                formData.lastName2,
                false,
                policiesTs,
                sensitiveTs
              );
            }
          } else {
            result = await signUp(
              formData.email,
              formData.password,
              formData.firstName,
              formData.lastName1,
              formData.lastName2,
              false,
              policiesTs,
              sensitiveTs
            );
          }
        }

        if (result.success) {
          if (mode === 'register') {
            setErrors({ submit: 'success:¡Cuenta creada! Revisa tu bandeja de entrada y haz clic en el enlace de confirmación para activar tu cuenta.' });
          } else {
            handleClose();
          }
        } else {
          const errorMessage = result.error || 'Error desconocido';
          if (mode === 'register' && (errorMessage.includes('ya está registrado') || errorMessage.includes('already') || errorMessage.includes('registered'))) {
            setErrors({
              submit: 'userExists:Este correo ya tiene una cuenta. Por favor, inicia sesión en su lugar.'
            });
          } else if (mode === 'login' && errorMessage.includes('no está confirmado')) {
            setErrors({
              submit: 'emailNotConfirmed:Tu email aún no está confirmado. Revisa tu bandeja de entrada y haz clic en el enlace de confirmación.'
            });
          } else {
            setErrors({ submit: errorMessage });
          }
        }
      }
    } catch (error) {
      setErrors({ submit: 'Error de conexión' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !loading) {
      handleClose();
    }
  };

  const handleSwitchMode = (newMode: 'login' | 'register' | 'forgot-password') => {
    setErrors({});
    onSwitchMode(newMode);
  };

  if (!isOpen) return null;

  // Desktop: login is narrower (no left illustration copy is shorter).
  const desktopWidthClass = mode === 'login' ? 'lg:w-[880px]' : 'lg:w-[960px]';

  return (
    <div
      className="fixed inset-0 z-modal flex items-stretch lg:items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200 lg:p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-modal-title"
    >
      <div
        ref={modalRef}
        className={`relative w-full h-[100dvh] lg:h-auto lg:max-h-[92vh] ${desktopWidthClass} lg:max-w-[95vw] lg:rounded-3xl overflow-hidden bg-white shadow-2xl shadow-stone-900/20 animate-in slide-in-from-bottom lg:zoom-in-95 duration-200 flex flex-col lg:flex-row`}
        onClick={(e) => e.stopPropagation()}
      >
        {mode === 'login' && (
          <LoginForm
            formData={formData}
            errors={errors}
            loading={loading}
            showPassword={showPassword}
            emailInputRef={emailInputRef as React.RefObject<HTMLInputElement>}
            onFieldChange={handleFieldChange}
            onTogglePassword={() => setShowPassword(!showPassword)}
            onSubmit={handleSubmit}
            onSwitchMode={handleSwitchMode}
            onGoogleSignIn={handleGoogleSignIn}
            onClose={handleClose}
          />
        )}

        {mode === 'register' && (
          <RegisterForm
            formData={formData}
            errors={errors}
            loading={loading}
            showPassword={showPassword}
            showConfirmPassword={showConfirmPassword}
            passwordStrength={passwordStrength}
            isInvitationFlow={isInvitationFlow}
            emailInputRef={emailInputRef as React.RefObject<HTMLInputElement>}
            policiesAccepted={policiesAccepted}
            setPoliciesAccepted={setPoliciesAccepted}
            sensitiveDataAccepted={sensitiveDataAccepted}
            setSensitiveDataAccepted={setSensitiveDataAccepted}
            onValidateConsent={validateConsent}
            onFieldChange={handleFieldChange}
            onTogglePassword={() => setShowPassword(!showPassword)}
            onToggleConfirmPassword={() => setShowConfirmPassword(!showConfirmPassword)}
            onSubmit={handleSubmit}
            onSwitchMode={handleSwitchMode}
            onGoogleSignIn={handleGoogleSignIn}
            onClose={handleClose}
          />
        )}

        {mode === 'forgot-password' && (
          <ForgotPasswordForm
            formData={formData}
            errors={errors}
            loading={loading}
            resetEmailSent={resetEmailSent}
            emailInputRef={emailInputRef as React.RefObject<HTMLInputElement>}
            onFieldChange={handleFieldChange}
            onSubmit={handleSubmit}
            onSwitchMode={handleSwitchMode}
            onClose={handleClose}
          />
        )}
      </div>
    </div>
  );
};

export default AuthModal;
